import React, { Component } from 'react';
import PropTypes from 'prop-types';
// eslint-disable-next-line lodash/import-scope -- directive added automatically by Shepherd migration
import isEqual from 'lodash/isEqual';
// eslint-disable-next-line lodash/import-scope -- directive added automatically by Shepherd migration
import get from 'lodash/get';
import classNames from 'classnames';

import nds from '@nerdwallet/base-styles/classes';
import styles from '@nerdwallet/base-styles/variables';
import { Text } from '@nerdwallet/currency';
import { formatCurrency } from '@nerdwallet/nw-client-lib/num';

import './results.less';

import C3 from '../c3-chart/c3-chart';

const principalColor = styles.colors.teal;
const interestColor = styles.colors.green;
const lineColor = styles.colors.neutralDarkest;

const constructC3Data = (data) => {
  const results = {
    columns: [data[1], data[0]], // Note the reversed order.
    type: 'bar',
    groups: [['Interest', 'Principal']],
    colors: {
      Principal: principalColor,
      Interest: interestColor,
    },
    order: null,
  };
  return results;
};

const constructC3Options = (data, size) => {
  const denominator = get(data, '0.length', 2);
  const ratios = {
    sm: 0.5 / denominator,
    md: 0.5 / denominator,
    lg: 0.5,
  };

  const widths = {
    sm: 272,
    md: 410,
    lg: 640,
  };

  return {
    bar: {
      width: {
        ratio: ratios[size],
      },
    },
    size: {
      height: size === 'sm' ? 200 : 370,
      width: widths[size],
    },
    grid: {
      y: {
        show: true,
      },
    },
    tooltip: {
      format: {
        title: (d) => `Year ${new Date().getUTCFullYear() + d}`,
        value: formatCurrency,
        name: (id) => `Total ${id}`,
      },
    },
    padding: {
      top: 10,
      bottom: 10,
      right: 15,
    },
    axis: {
      x: {
        label: {
          text: 'Year',
          position: 'outer-center',
        },
        tick: {
          count: size === 'lg' ? undefined : 3,
          format: (x) => {
            if (x === 0) {
              return 'Now';
            }
            const year = new Date().getUTCFullYear();
            return year + Math.round(x);
          },
        },
        color: lineColor,
      },
      y: {
        tick: {
          count: 6,
          format: (y) => {
            // Fix at 2 sigfigs (poor mans value snapping)
            const magnitude = Math.max(1, Math.floor(Math.log10(y)));
            const scaleFactor = 10 ** magnitude / 10;
            return formatCurrency(Math.round(y / scaleFactor) * scaleFactor);
          },
        },
        color: lineColor,
      },
    },
    legend: {
      show: false,
    },
  };
};

class Results extends Component {
  shouldComponentUpdate(nextProps) {
    return !isEqual(nextProps, this.props);
  }

  getTotal() {
    if (this.props.data == null || this.props.data.length === 0) {
      return 0;
    }

    const [principals, interests] = this.props.data;
    return principals[principals.length - 1] + interests[interests.length - 1];
  }

  // eslint-disable-next-line class-methods-use-this
  renderLegend() {
    const swabStyle = {
      width: 12,
      height: 12,
      borderRadius: 12,
      display: 'inline-block',
      verticalAlign: 'middle',
      marginRight: 4,
    };
    return (
      <div className={nds('text-align-left')}>
        <span className={nds('margin-right-3')}>
          <span style={{ ...swabStyle, background: principalColor }} />
          <Text small inline>
            Principal
          </Text>
        </span>
        <span>
          <span style={{ ...swabStyle, background: interestColor }} />
          <Text small inline>
            Interest
          </Text>
        </span>
      </div>
    );
  }

  render() {
    return (
      <div className="results-module">
        <Text bold className={classNames([nds('text-align-left')])}>
          Estimated Future Balance: {formatCurrency(this.getTotal())}
        </Text>
        {this.renderLegend()}
        <div className="results-module__chart-wrapper">
          <C3
            data={constructC3Data(this.props.data)}
            options={constructC3Options(this.props.data, this.props.size)}
            uniqueId="results-chart"
          />
        </div>
      </div>
    );
  }
}

// eslint-disable-next-line react/static-property-placement -- directive added automatically by Shepherd migration
Results.displayName = 'ResultsUI';

// eslint-disable-next-line react/static-property-placement -- directive added automatically by Shepherd migration
Results.defaultProps = {
  data: [
    ['Principal', 1, 2, 0, 0, 0, 0, 0, 0, 0, 2],
    ['Interest', 1, 4, 0, 0, 0, 0, 0, 0, 0, 4],
  ],
  size: 'sm',
};

// eslint-disable-next-line react/static-property-placement -- directive added automatically by Shepherd migration
Results.propTypes = {
  data: PropTypes.array,
  className: PropTypes.string,
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
};

export default Results;
